<template>
  <div>
    <template slot="header">
      <div class="row">
        <div class="col-6">
          <h3 class="mb-0">
            {{ $t("CASH_REGISTER_READINGS.CASH_REGISTER_READINGS_LIST") }}
          </h3>
        </div>
        <div class="col-6 text-right">
          <base-button
            type="primary"
            icon
            size="sm"
            @click="addCashRegisterReading()"
            v-if="
              $currentUserCan($permissions.PERM_CREATE_CASH_REGISTER_READINGS)
            "
          >
            <span class="btn-inner--icon">
              <i class="fas fa-plus"></i>
            </span>
            <span class="btn-inner--text">
              {{ $t("CASH_REGISTER_READINGS.ADD_CASH_REGISTER_READING") }}
            </span>
          </base-button>
        </div>
      </div>
    </template>
    <div>
      <div
        class="col-12 d-flex flex-wrap filter-row"
      >
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <div>
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.CASH_REGISTER_READING')"
            clearable
          />
        </div>

        <el-select :placeholder="$t('COMMON.STATUS')" v-model="selectedStatus">
          <el-option :value="0" :label="$t('COMMON.ALL_STATUSES')"> </el-option>
          <el-option
            v-for="status in statusesOptions"
            :key="status"
            :value="status"
            :label="$t(`CASH_REGISTER_READINGS.STATUS_${status}`)"
          >
          </el-option>
        </el-select>

        <base-input class="dates" :placeholder="$t('COMMON.DATES_PLACEHOLDER')">
          <flat-picker
            :config="{
              allowInput: true,
              mode: 'range',
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
            v-model="dateRange"
            :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
          >
          </flat-picker>
        </base-input>

        <establishment-selector
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          @establishmentChanged="
            (establishmentId) => (selectedEstablishment = establishmentId)
          "
        />

        <organization-selector
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />
      </div>

      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="cashRegisterReadings"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>

        <el-table-column
          :label="$t('CASH_REGISTER_READINGS.STARTS_AT')"
          prop="code"
          sortable="custom"
          min-width="150px"
        >
          <template v-slot="{ row }">
            <span>
              {{ `${$dateFormat(row.starts_at, 'LL')}` }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.ESTABLISHMENTS')"
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          min-width="300px"
        >
          <template v-slot="{ row }">
            <object-link :object="row.establishment" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('CASH_REGISTER_READINGS.CASH_BALANCE')"
          min-width="150px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row.total_amount) }}
          </template>
        </el-table-column>
        
        <el-table-column
          :label='$t("CASH_REGISTER_READINGS.AMOUNT_GAP")'
          min-width="150px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row.amount_gap) }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('CASH_REGISTER_READINGS.REGISTER_ERRORS')"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ $formatCurrency(row.register_errors) }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          min-width="100px"
        >
          <template slot-scope="{ row }">
            <cash-register-reading-status-badge :cashRegisterReading="row" />
          </template>
        </el-table-column>

        <base-input class="dates" :placeholder="$t('COMMON.DATES_PLACEHOLDER')">
          <flat-picker
            :config="{
              allowInput: true,
              mode: 'range',
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
            v-model="dateRange"
            :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
          >
          </flat-picker>
        </base-input>

        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('COMMON.CREATED_AT')"
          prop="created_at"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ $dateFormat(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column min-width="100px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_CASH_REGISTERS)"
            >
              <a
                type="text"
                @click="viewCashRegisterReading(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_EDIT_CASH_REGISTERS)"
            >
              <a
                type="text"
                @click="editCashRegisterReading(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_DELETE_CASH_REGISTERS)"
            >
              <a
                type="text"
                @click="deleteCashRegisterReading(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import CashRegisterReadingStatusBadge from "./CashRegisterReadingStatusBadge.vue";
import { cashRegisterReadingStatusesOption } from "@/constants/cashRegisterReading";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "cash-register-reading-list-table",

  components: {
    BasePagination,
    OrganizationSelector,
    EstablishmentSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    CashRegisterReadingStatusBadge,
    flatPicker,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      dateRange: null,
      cashRegisterReadings: [],
      loading: true,
      filterEstablishment: null,
      selectedEstablishment: null,
      selectedOrganization: null,
      selectedStatus: null,
      statusesOptions: cashRegisterReadingStatusesOption,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedTags: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedLocations: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    dateRange: {
      handler: "getListDebounced",
      immediate: false,
      deep: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        const vDateRange = this.dateRange
          ? this.dateRange.split(this.$flatPickrLocale().rangeSeparator)
          : [];
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            ...(vDateRange.length == 2
              ? {
                  dateRange: [
                    vDateRange[0] + " 00:00:00",
                    vDateRange[1] + " 23:59:59",
                  ],
                }
              : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,establishment",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.selectedEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.selectedEstablishment,
            },
          };
        }
        if (this.filterEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.filterEstablishment,
            },
          };
        }
        if (this.selectedTags) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              tags: this.selectedTags.map((item) => item.id),
            },
          };
        }
        if (this.selectedStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              status: this.selectedStatus,
            },
          };
        }
        if (this.selectedLocations) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              allowedLocations: this.selectedLocations.map((loc) => loc.id),
            },
          };
        }

        await this.$store.dispatch("cashRegisterReadings/list", params);
        this.cashRegisterReadings =
          this.$store.getters["cashRegisterReadings/list"];
        this.total = this.$store.getters["cashRegisterReadings/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteCashRegisterReading(id) {
      const confirmation = await swal.fire({
        title: this.$t(
          "CASH_REGISTER_READINGS.DELETE_THIS_CASH_REGISTER_READING"
        ),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("cashRegisterReadings/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t(
              "CASH_REGISTER_READINGS.CASH_REGISTER_READING_DELETED"
            ),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewCashRegisterReading(cashRegisterReading) {
      this.$router.push({
        name: "View CashRegisterReading",
        params: { id: cashRegisterReading.id },
      });
    },

    async editCashRegisterReading(row) {
      this.$router.push({
        name: "Edit CashRegisterReading",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
